<template>
  <div class="pdp">
    <div class="pdp-head">
      <span class="pdp-title">仿真资源详情</span>
      <button type="button" class="pdp-x" title="关闭" @click="sceneStore.clearPhysicsEditSelection()">×</button>
    </div>
    <div v-if="!physics" class="pdp-empty">请先初始化仿真配置</div>
    <template v-else-if="sel">
      <!-- zone -->
      <div v-if="sel.kind === 'zone' && zone" class="pdp-body">
        <label class="pdp-field"><span>名称</span><input v-model="zoneDraft.name" class="pdp-input" @change="commitZone" /></label>
        <label class="pdp-field"><span>体积 m³</span><input v-model.number="zoneDraft.volume" type="number" min="0" step="any" class="pdp-input" @change="commitZone" /></label>
        <label class="pdp-field"><span>初始温度 ℃</span><input v-model.number="zoneDraft.initTempC" type="number" step="any" class="pdp-input" @change="commitZone" /></label>
        <label class="pdp-field"><span>初始湿度 %</span><input v-model.number="zoneDraft.initRh" type="number" min="0" max="100" step="any" class="pdp-input" @change="commitZone" /></label>
        <button type="button" class="pdp-del" @click="remove('zone')">删除区域</button>
      </div>
      <!-- edge -->
      <div v-else-if="sel.kind === 'edge' && edge" class="pdp-body">
        <p class="pdp-id-hint">内部 ID：<code>{{ edge.id }}</code></p>
        <label class="pdp-field"><span>名称</span><input v-model="edgeDraft.name" class="pdp-input" @change="commitEdge" /></label>
        <label class="pdp-field"><span>起点区域</span>
          <select v-model="edgeDraft.from" class="pdp-input" @change="commitEdge">
            <option value="">（选择区域）</option>
            <option v-for="z in zoneOptions" :key="z.id" :value="z.id">{{ z.name }}（{{ z.id }}）</option>
          </select>
        </label>
        <label class="pdp-field"><span>终点区域</span>
          <select v-model="edgeDraft.to" class="pdp-input" @change="commitEdge">
            <option value="">（选择区域）</option>
            <option v-for="z in zoneOptions" :key="z.id" :value="z.id">{{ z.name }}（{{ z.id }}）</option>
          </select>
        </label>
        <label class="pdp-field"><span>介质</span>
          <select v-model="edgeDraft.medium" class="pdp-input" @change="commitEdge">
            <option value="water">{{ flowMediumLabel('water') }}</option>
            <option value="air">{{ flowMediumLabel('air') }}</option>
            <option value="steam">{{ flowMediumLabel('steam') }}</option>
          </select>
        </label>
        <label class="pdp-field"><span>管径 m</span><input v-model.number="edgeDraft.diameterM" type="number" min="0" step="any" class="pdp-input" @change="commitEdge" /></label>
        <label class="pdp-field"><span>长度 m</span><input v-model.number="edgeDraft.lengthM" type="number" min="0" step="any" class="pdp-input" @change="commitEdge" /></label>
        <button type="button" class="pdp-del" @click="remove('edge')">删除管段</button>
      </div>
      <!-- source -->
      <div v-else-if="sel.kind === 'source' && source" class="pdp-body">
        <label class="pdp-field"><span>名称</span><input v-model="sourceDraft.name" class="pdp-input" @change="commitSource" /></label>
        <label class="pdp-field"><span>类型</span>
          <select v-model="sourceDraft.type" class="pdp-input" @change="commitSource">
            <option value="heat">{{ physicsSourceTypeLabel('heat') }}</option>
            <option value="humidity">{{ physicsSourceTypeLabel('humidity') }}</option>
            <option value="fan">{{ physicsSourceTypeLabel('fan') }}</option>
            <option value="valve">{{ physicsSourceTypeLabel('valve') }}</option>
          </select>
        </label>
        <label class="pdp-field"><span>关联对象（可选）</span>
          <select v-model="sourceDraft.objectRef" class="pdp-input" @change="commitSource">
            <option value="">（不绑定对象）</option>
            <option v-for="o in objectOptions" :key="o.id" :value="o.id">{{ o.name }} ({{ o.id }})</option>
          </select>
        </label>
        <label class="pdp-field"><span>关联区域（可选）</span>
          <select v-model="sourceDraft.zoneId" class="pdp-input" @change="commitSource">
            <option value="">（不绑定区域）</option>
            <option v-for="z in zoneOptions" :key="z.id" :value="z.id">{{ z.name }} ({{ z.id }})</option>
          </select>
        </label>
        <label class="pdp-check"><input v-model="sourceDraft.enabled" type="checkbox" @change="commitSource" /> 启用</label>
        <button type="button" class="pdp-del" @click="remove('source')">删除源项</button>
      </div>
      <!-- binding -->
      <div v-else-if="sel.kind === 'binding' && binding" class="pdp-body">
        <label class="pdp-field"><span>源对象</span>
          <select v-model="bindingDraft.sourceObjectId" class="pdp-input" @change="commitBinding">
            <option value="">（选择对象）</option>
            <option v-for="o in objectOptions" :key="o.id" :value="o.id">{{ o.name }}（{{ o.id }}）</option>
          </select>
        </label>
        <label class="pdp-field"><span>源数据字段</span><input v-model="bindingDraft.sourceMetric" class="pdp-input" placeholder="如设备快照中的指标键名" @change="commitBinding" /></label>
        <label class="pdp-field"><span>目标类型</span>
          <select v-model="bindingDraft.targetType" class="pdp-input" @change="onTargetTypeChange">
            <option value="">（选择类型）</option>
            <option value="zone">区域</option>
            <option value="edge">管段</option>
            <option value="source">源项</option>
          </select>
        </label>
        <label class="pdp-field"><span>目标</span>
          <select v-model="bindingDraft.targetId" class="pdp-input" @change="commitBinding">
            <option value="">（选择目标）</option>
            <option v-for="opt in targetIdOptions" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
          </select>
        </label>
        <label class="pdp-field"><span>目标数据字段</span><input v-model="bindingDraft.targetMetric" class="pdp-input" placeholder="如仿真资源侧接收的键名" @change="commitBinding" /></label>
        <button type="button" class="pdp-del" @click="remove('binding')">删除绑定</button>
      </div>
      <div v-else class="pdp-empty">条目已不存在</div>
    </template>
    <div v-else class="pdp-empty">请先在仿真资源列表中点击一个条目进行编辑</div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useSceneStore } from '@/stores/scene'
import type { FlowMedium, PhysicsSourceType } from '@/types/scene'
import { flowMediumLabel, physicsSourceTypeLabel } from '@/utils/physicsUiLabels'

const props = defineProps<{ onHistoryPush: () => void }>()
const sceneStore = useSceneStore()

const sel = computed(() => sceneStore.physicsEditSelection)
const physics = computed(() => sceneStore.activeScene()?.physicsConfig)

const zone = computed(() => {
  if (sel.value?.kind !== 'zone') return null
  return physics.value?.zones.find((z) => z.id === sel.value!.id) ?? null
})
const edge = computed(() => {
  if (sel.value?.kind !== 'edge') return null
  return physics.value?.flowNetwork.edges.find((e) => e.id === sel.value!.id) ?? null
})
const source = computed(() => {
  if (sel.value?.kind !== 'source') return null
  return physics.value?.sources.find((s) => s.id === sel.value!.id) ?? null
})
const binding = computed(() => {
  if (sel.value?.kind !== 'binding') return null
  return physics.value?.bindings.find((b) => b.id === sel.value!.id) ?? null
})

/** 场景对象列表，供源对象 ID / objectRef 下拉使用 */
const objectOptions = computed(() =>
  (sceneStore.activeScene()?.objects ?? []).map((o) => ({ id: o.id, name: o.name || o.id }))
)

/** 当前所有 zone，供管段端点 / source zoneId / binding targetId 选择 */
const zoneOptions = computed(() =>
  (physics.value?.zones ?? []).map((z) => ({ id: z.id, name: z.name || z.id }))
)

/** binding 目标 ID 选项，随 targetType 切换 */
const targetIdOptions = computed((): Array<{ id: string; label: string }> => {
  const t = bindingDraft.value.targetType
  if (t === 'zone') return zoneOptions.value.map((z) => ({ id: z.id, label: `${z.name} (${z.id})` }))
  if (t === 'edge')
    return (physics.value?.flowNetwork.edges ?? []).map((e) => ({ id: e.id, label: e.id }))
  if (t === 'source')
    return (physics.value?.sources ?? []).map((s) => ({ id: s.id, label: s.name || s.id }))
  return []
})

const zoneDraft = ref({ name: '', volume: 0, initTempC: 0, initRh: 0 })
const edgeDraft = ref({ name: '', from: '', to: '', medium: 'water' as FlowMedium, diameterM: 0, lengthM: 0 })
const sourceDraft = ref({
  name: '',
  type: 'heat' as PhysicsSourceType,
  objectRef: '',
  zoneId: '',
  enabled: true,
})
const bindingDraft = ref({
  sourceObjectId: '',
  sourceMetric: '',
  targetType: '',
  targetId: '',
  targetMetric: '',
})

watch(
  zone,
  (z) => {
    if (!z) return
    zoneDraft.value = { name: z.name, volume: z.volume, initTempC: z.initTempC, initRh: z.initRh }
  },
  { immediate: true }
)

watch(
  edge,
  (e) => {
    if (!e) return
    edgeDraft.value = {
      name: e.name ?? '',
      from: e.from,
      to: e.to,
      medium: e.medium,
      diameterM: e.diameterM,
      lengthM: e.lengthM,
    }
  },
  { immediate: true }
)

watch(
  source,
  (s) => {
    if (!s) return
    sourceDraft.value = {
      name: s.name,
      type: s.type,
      objectRef: s.objectRef ?? '',
      zoneId: s.zoneId ?? '',
      enabled: s.enabled !== false,
    }
  },
  { immediate: true }
)

watch(
  binding,
  (b) => {
    if (!b) return
    bindingDraft.value = {
      sourceObjectId: b.sourceObjectId,
      sourceMetric: b.sourceMetric,
      targetType: b.targetType,
      targetId: b.targetId,
      targetMetric: b.targetMetric,
    }
  },
  { immediate: true }
)

function commitZone() {
  const id = sel.value?.kind === 'zone' ? sel.value.id : null
  if (!id) return
  props.onHistoryPush()
  sceneStore.updatePhysicsZone(id, { ...zoneDraft.value })
}

function commitEdge() {
  const cur = sel.value?.kind === 'edge' ? sel.value.id : null
  if (!cur) return
  props.onHistoryPush()
  const d = edgeDraft.value
  sceneStore.updatePhysicsFlowEdge(cur, {
    name: d.name.trim() || undefined,
    from: d.from,
    to: d.to,
    medium: d.medium,
    diameterM: d.diameterM,
    lengthM: d.lengthM,
  })
}

function commitSource() {
  const id = sel.value?.kind === 'source' ? sel.value.id : null
  if (!id) return
  props.onHistoryPush()
  const d = sourceDraft.value
  sceneStore.updatePhysicsSource(id, {
    name: d.name,
    type: d.type,
    objectRef: d.objectRef || undefined,
    zoneId: d.zoneId || undefined,
    enabled: d.enabled,
  })
}

function commitBinding() {
  const id = sel.value?.kind === 'binding' ? sel.value.id : null
  if (!id) return
  props.onHistoryPush()
  sceneStore.updatePhysicsBinding(id, { ...bindingDraft.value })
}

/** targetType 变更时清空 targetId（旧 ID 对新类型无效） */
function onTargetTypeChange() {
  bindingDraft.value.targetId = ''
  commitBinding()
}

function remove(kind: 'zone' | 'edge' | 'source' | 'binding') {
  const id = sel.value?.id
  if (!id || sel.value.kind !== kind) return
  props.onHistoryPush()
  sceneStore.removePhysicsEntity(kind, id)
}
</script>

<style scoped>
.pdp {
  width: 100%;
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #111822;
  border-top: 1px solid #2c3f5a;
  font-size: 12px;
  color: #c8d8f0;
}
.pdp-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #2a3a50;
}
.pdp-title {
  font-weight: 600;
  color: #e8f2ff;
}
.pdp-x {
  border: none;
  background: transparent;
  color: #8a9eb8;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.pdp-body {
  flex: 1 1 0%;
  min-height: 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-x: hidden;
  overflow-y: auto;
}
.pdp-empty {
  flex: 1 1 0%;
  min-height: 0;
  padding: 16px 12px;
  color: #6b829e;
  line-height: 1.5;
  overflow-x: hidden;
  overflow-y: auto;
}
.pdp-id-hint {
  margin: 0 0 4px;
  font-size: 11px;
  color: #6b829e;
}
.pdp-id-hint code {
  color: #8ec5ff;
}
.pdp-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #8aa0b8;
}
.pdp-field span {
  font-size: 11px;
}
.pdp-input {
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #324a69;
  background: #0e1724;
  color: #e8f2ff;
  font-size: 12px;
}
.pdp-check {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #9eb8d8;
}
.pdp-del {
  margin-top: 8px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #7f1d1d;
  background: #3f1212;
  color: #fecaca;
  cursor: pointer;
  font-size: 12px;
}
.pdp-del:hover {
  background: #5c1818;
}
</style>
